import React, { useEffect, useRef, useState } from 'react';
import { App as AntdApp, Typography, Button, Divider, Space, InputNumber, Select, theme, Upload } from 'antd';
import { DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import BwmInput from '../components/BwmInput';
import BwmMatrix from '../components/BwmMatrix';
import LFuzzyMatrix, { convertMatrixToValues } from '../components/BwmFuzzyMatrix';
import FuzzyMatrix from '../components/BwmFuzzyMatrixTfn';
import Results from '../components/Results';
import { BwmWeights } from '../api/fetchWeights';
import {
  importBWMMatrixFlexibleFile,
} from '../utils/matrixImport';
import { validateBWM, validateFuzzyBWM, validateLinguisticFuzzyBWM } from '../utils/validators';
import { exportMatrixXlsx } from '../utils/matrixExport';

const { Title, Text } = Typography;

function Bwm({ variant, methodSelector, methodChanged, criteriaCount, criteria, updateCriteria }) {
  const isImportingRef = useRef(false);
  const [stage, setStage] = useState('number'); // number | text | null | edit
  const [localCount, setLocalCount] = useState(criteria.length || criteriaCount || 0);
  const [tempCriteria, setTempCriteria] = useState([]);
  const [matrix, setMatrix] = useState([]);

  const [crisp_weights, setWeights] = useState([]);
  const [lower_weights, setLWeights] = useState([]);
  const [upper_weights, setUWeights] = useState([]);
  const [sorted_criteria, setSortedCriteria] = useState([]);
  const [ci, setCi] = useState(null);
  const [cr, setCr] = useState(null);
  const [best, setBest] = useState(null);
  const [worst, setWorst] = useState(null);
  const [inconsistency_ratios, setInconsistencyRatio] = useState([]);
  const [extra, setExtra] = useState({});
  const [fileName] = useState('');

  const { token } = theme.useToken();
  const { message: messageApi, modal } = AntdApp.useApp();

  const templateHrefMap = {
    linear: `${process.env.PUBLIC_URL}/templates/BWM_matrix_template.xlsx`,
    nonlinear: `${process.env.PUBLIC_URL}/templates/BWM_matrix_template.xlsx`,
    fuzzy: `${process.env.PUBLIC_URL}/templates/BWM_fuzzy_tfn_template.xlsx`,
    'linguistic fuzzy': `${process.env.PUBLIC_URL}/templates/BWM_linguistic_template.xlsx`,
  };

  const templateHref = templateHrefMap[variant];

  const resetResults = () => {
    setWeights([]);
    setLWeights([]);
    setUWeights([]);
    setSortedCriteria([]);
    setCi(null);
    setCr(null);
    setInconsistencyRatio([]);
    setExtra({});
  };

  const handleImport = async (file) => {
    try {
      const {
        criteria: parsedCriteria,
        matrix: parsedMatrix,
        bestIdx: detectedBestIdx,
        worstIdx: detectedWorstIdx,
      } = await importBWMMatrixFlexibleFile(file, variant);

      let validation;
      if (variant === 'linear' || variant === 'nonlinear') {
        validation = validateBWM(parsedCriteria, parsedMatrix);
      } else if (variant === 'fuzzy') {
        validation = validateFuzzyBWM(parsedCriteria, parsedMatrix);
      } else if (variant === 'linguistic fuzzy') {
        validation = validateLinguisticFuzzyBWM(parsedCriteria, parsedMatrix);
      } else {
        validation = { ok: true, errors: [] };
      }

      if (!validation?.ok) {
        modal.error({
          title: 'Invalid BWM matrix',
          content: <div>{validation?.errors?.map((e, i) => <div key={i}>- {e}</div>)}</div>,
          width: 560,
        });
        return false;
      }
      const detectedBest =
        Number.isInteger(detectedBestIdx) && detectedBestIdx >= 0
          ? parsedCriteria[detectedBestIdx]
          : null;
      const detectedWorst =
        Number.isInteger(detectedWorstIdx) && detectedWorstIdx >= 0
          ? parsedCriteria[detectedWorstIdx]
          : null;

      isImportingRef.current = true;
      updateCriteria(parsedCriteria);
      setLocalCount(parsedCriteria.length);
      setMatrix(parsedMatrix);
      setBest(detectedBest);
      setWorst(detectedWorst);
      setStage(null);
      resetResults();
      if (detectedBest && detectedWorst) {
        messageApi.success('BWM matrix imported successfully. Best and worst were detected automatically.');
      } else {
        messageApi.success('BWM matrix imported. Please select best and worst criteria.');
      }
    } catch (e) {
      modal.error({ title: 'Import error', content: e.message || String(e) });
    }

    return false;
  };

  const handleSetCriteriaNumber = () => {
    const count = Math.max(2, Math.min(100, localCount));
    setLocalCount(count);
    updateCriteria(Array.from({ length: count }, (_, i) => `Criterion ${i + 1}`));
    setStage('text');
  };

  const worstIdx = worst ? criteria.indexOf(worst) : -1;
  const bestIdx = best ? criteria.indexOf(best) : -1;

  const handleConfirmCriteria = () => {
    const count = criteria.length;
    const diagonalValue =
      variant === 'linguistic fuzzy' ? 'EI' : variant === 'fuzzy' ? [1, 1, 1] : 1;
    const initialValue =
      variant === 'linguistic fuzzy' ? 'EI' : variant === 'fuzzy' ? [1, 1, 1] : 1;

    setMatrix(
      Array.from({ length: count }, (_, i) =>
        Array.from({ length: count }, (_, j) => (i === j ? diagonalValue : initialValue))
      )
    );
    setStage(null);
  };

  useEffect(() => {
    if (!criteria.length) return;
    if (isImportingRef.current) {
      isImportingRef.current = false;
      return;
    }

    const count = criteria.length;
    const diagonalValue =
      variant === 'linguistic fuzzy' ? 'EI' : variant === 'fuzzy' ? [1, 1, 1] : 1;
    const initialValue =
      variant === 'linguistic fuzzy' ? 'EI' : variant === 'fuzzy' ? [1, 1, 1] : 1;

    setMatrix(
      Array.from({ length: count }, (_, i) =>
        Array.from({ length: count }, (_, j) => (i === j ? diagonalValue : initialValue))
      )
    );
    setBest(null);
    setWorst(null);
    resetResults();
    setStage(null);
  }, [criteria.length, methodChanged, variant]);

  const handleSubmit = async () => {
    const activeCriteria = criteria.filter((c) => c.trim() !== '');
    if (activeCriteria.length < 2) {
      alert('Please define at least two valid criteria.');
      return;
    }

    if (bestIdx == null || bestIdx < 0 || worstIdx == null || worstIdx < 0) {
      alert('Please select both best and worst criteria.');
      return;
    }

    const numericMatrix = convertMatrixToValues(matrix);
    const processedMatrix = variant === 'linguistic fuzzy' ? numericMatrix : matrix;

    const payload = {
      variant,
      n: criteria.length,
      criteria,
      bestIdx,
      worstIdx,
      bestRow: [...processedMatrix[bestIdx]],
      worstCol: processedMatrix.map((row) => row[worstIdx]),
    };

    try {
      const {
        crisp_weights: resultWeights,
        lower_weights: resultLower,
        upper_weights: resultUpper,
        sorted_criteria: resultSorted,
        ci: resultCi,
        cr: resultCr,
        inconsistency_ratios: resultRatios,
        extra: resultExtra,
      } = await BwmWeights(payload);

      setWeights(resultWeights || []);
      setLWeights(resultLower || []);
      setUWeights(resultUpper || []);
      setSortedCriteria(resultSorted || []);
      setCi(resultCi);
      setCr(resultCr);
      setInconsistencyRatio(resultRatios || []);
      setExtra(resultExtra || {});
    } catch (err) {
      alert(err.message);
    }
  };

  const handleReset = () => {
    setStage('number');
    updateCriteria([]);
    setLocalCount(0);
    setBest(null);
    setWorst(null);
    setMatrix([]);
    resetResults();
  };

  const handleEdit = () => {
    setTempCriteria([...criteria]);
    setStage('edit');
  };

  const handleSaveEdit = () => {
    const count = tempCriteria.length;
    const diagonalValue =
      variant === 'linguistic fuzzy' ? 'EI' : variant === 'fuzzy' ? [1, 1, 1] : 1;
    const defaultValue =
      variant === 'linguistic fuzzy' ? 'EI' : variant === 'fuzzy' ? [1, 1, 1] : 1;

    const newMatrix = Array.from({ length: count }, (_, i) =>
      Array.from({ length: count }, (_, j) => {
        if (i === j) return diagonalValue;
        return matrix?.[i]?.[j] ?? defaultValue;
      })
    );

    updateCriteria([...tempCriteria]);
    setMatrix(newMatrix);
    setBest(null);
    setWorst(null);
    resetResults();
    setStage(null);
  };

  const handleExportXlsx = () => {
    exportMatrixXlsx({
      method: 'bwm',
      variant,
      criteria,
      matrix,
      bestIdx,
      worstIdx,
      filename: fileName || undefined,
    });
  };

  const headerExtra = (
    <Button icon={<DownloadOutlined />} onClick={handleExportXlsx}>
      Export input matrix (.xlsx)
    </Button>
  );

  const numericMatrixForViz = variant === 'linguistic fuzzy' ? convertMatrixToValues(matrix) : matrix;

  const bestRowForViz =
    bestIdx >= 0 && Array.isArray(numericMatrixForViz[bestIdx])
      ? [...numericMatrixForViz[bestIdx]]
      : null;

  const worstColForViz =
    worstIdx >= 0 && Array.isArray(numericMatrixForViz)
      ? numericMatrixForViz.map((row) => row[worstIdx])
      : null;

  return (
    <div
      style={{
        display: 'flex',
        minHeight: '100vh',
        background: token.colorBgLayout,
      }}
    >
      <div
        style={{
          width: 420,
          background: token.colorBgContainer,
          padding: token.paddingLG,
          borderRight: `1px solid ${token.colorSplit}`,
        }}
      >
        <Title level={2} style={{ marginTop: 0, marginBottom: 0 }}>
          BWM Pairwise Analysis
        </Title>
        <Text type="secondary" style={{ display: 'block', marginTop: 6 }}>
          Configure criteria and calculate BWM weights with consistency diagnostics.
        </Text>

        <div style={{ marginTop: 24, marginBottom: 24 }}>
          {methodSelector}

          {(variant === 'linear' || variant === 'nonlinear') && (
          <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            The BWM {variant} calculation is grounded in the foundational research detailed in the 
            publication available at:{' '} 
            <a href="https://doi.org/10.1016/j.omega.2015.12.001" target="_blank" rel="noreferrer">
              https://doi.org/10.1016/j.omega.2015.12.001
            </a>
          </Typography.Text>
          )}

          {(variant === 'linguistic fuzzy') && (
          <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            The BWM {variant} calculation is grounded in the foundational research detailed in the 
            publication available at:{' '} 
            <a href="https://doi.org/10.1016/j.knosys.2017.01.010" target="_blank" rel="noreferrer">
              https://doi.org/10.1016/j.knosys.2017.01.010
            </a>
          </Typography.Text>
          )}

          {(variant === 'fuzzy') && (
          <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            The BWM {variant} calculation is grounded in the foundational research detailed in the 
            publication available at:{' '} 
            {/* <a href="https://doi.org/10.1016/j.knosys.2017.01.010" target="_blank" rel="noreferrer">
              https://doi.org/10.1016/j.knosys.2017.01.010
            </a> */}
            <a href="https://doi.org/10.1016/j.ins.2020.09.014" target="_blank" rel="noreferrer">
              https://doi.org/10.1016/j.ins.2020.09.014
            </a>
          </Typography.Text>
          )}

        </div>

        <div style={{ margin: '8px 0 10px' }}>
          <Upload
            beforeUpload={handleImport}
            showUploadList={false}
            multiple={false}
            maxCount={1}
            accept={[
              'text/csv',
              'application/vnd.ms-excel',
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              '.csv',
              '.CSV',
              '.xlsx',
              '.XLSX',
            ].join(',')}
          >
            <Button icon={<UploadOutlined />} style={{ marginTop: 16, marginBottom: 0 }}>
              Import BWM matrix (CSV/XLSX)
            </Button>
          </Upload>

          <Typography.Link
            href={templateHref}
            download
            style={{ display: 'inline-block', marginLeft: 10, textDecoration: 'underline' }}
          >
            Download BWM template
          </Typography.Link>
        </div>

        <div style={{ marginBottom: 24 }}>
          <Text strong>Number of criteria: </Text>
          {stage === 'number' ? (
            <Space style={{ marginTop: 8 }}>
              <InputNumber
                min={2}
                max={100}
                value={localCount}
                onChange={(value) => setLocalCount(value ?? 0)}
              />
              <Button type="primary" onClick={handleSetCriteriaNumber}>
                Initialize criteria
              </Button>
            </Space>
          ) : (
            <Space style={{ marginLeft: 8 }}>
              <Text>{criteria.length}</Text>
            </Space>
          )}
        </div>

        {stage === 'text' && (
          <>
            <BwmInput criteria={criteria} setCriteria={updateCriteria} editable allowAddRemove={false} />
            <Button type="primary" block style={{ marginTop: 16 }} onClick={handleConfirmCriteria}>
              Generate matrix
            </Button>
          </>
        )}

        {(stage === null || stage === 'edit') && (
          <>
            <BwmInput
              criteria={stage === 'edit' ? tempCriteria : criteria}
              setCriteria={stage === 'edit' ? setTempCriteria : updateCriteria}
              editable={stage === 'edit' || stage === 'text'}
              allowAddRemove={stage === 'edit'}
            />
            <div
              style={{
                marginTop: 16,
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: 8,
                width: '100%',
              }}
            >
              {stage === null ? (
                <Button onClick={handleEdit} style={{ width: '100%' }}>
                  Edit criteria
                </Button>
              ) : (
                <Button onClick={handleSaveEdit} style={{ width: '100%' }}>
                  Save changes
                </Button>
              )}

              <Button onClick={handleReset} style={{ width: '100%' }}>
                Reset
              </Button>
            </div>
          </>
        )}

        {stage === null && criteria.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <Text strong>Best criterion</Text>
            <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
              <Select
                placeholder="Select best criterion"
                value={best}
                onChange={setBest}
                options={criteria.map((c) => ({ value: c, label: c }))}
                style={{ width: '100%' }}
              />

              <Text strong>Worst criterion</Text>
              <Select
                placeholder="Select worst criterion"
                value={worst}
                onChange={setWorst}
                options={criteria.filter((c) => c !== best).map((c) => ({ value: c, label: c }))}
                style={{ width: '100%' }}
              />
            </Space>
          </div>
        )}

        {stage === null && !!best && !!worst && (
          <Button type="primary" block onClick={handleSubmit} style={{ marginTop: 16 }}>
            Calculate
          </Button>
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0, padding: token.paddingLG, background: token.colorBgLayout }}>
        {!!best && !!worst && matrix.length > 0 && (
          <>
            <div style={{ width: '100%', overflowX: 'auto' }}>
              {variant === 'linguistic fuzzy' ? (
                <LFuzzyMatrix
                  matrix={matrix}
                  setMatrix={setMatrix}
                  criteria={criteria}
                  bestIdx={bestIdx}
                  worstIdx={worstIdx}
                  extra={headerExtra}
                />
              ) : variant === 'fuzzy' ? (
                <FuzzyMatrix
                  matrix={matrix}
                  setMatrix={setMatrix}
                  criteria={criteria}
                  bestIdx={bestIdx}
                  worstIdx={worstIdx}
                  extra={headerExtra}
                />
              ) : (
                <BwmMatrix
                  matrix={matrix}
                  setMatrix={setMatrix}
                  criteria={criteria}
                  bestIdx={bestIdx}
                  worstIdx={worstIdx}
                  extra={headerExtra}
                />
              )}
            </div>

            {crisp_weights.length > 0 && (
              <>
                <Divider />
                <Results
                  method="bwm"
                  variant={variant?.toLowerCase?.()}
                  crisp_weights={crisp_weights}
                  lower_weights={lower_weights}
                  upper_weights={upper_weights}
                  criteria={criteria}
                  bestIdx={bestIdx}
                  worstIdx={worstIdx}
                  bestRow={bestRowForViz}
                  worstCol={worstColForViz}
                  sorted_criteria={sorted_criteria}
                  ci={ci}
                  cr={cr}
                  inconsistency_ratios={inconsistency_ratios}
                  matrix={matrix}
                  extra={extra}
                />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Bwm;
